package devxertech.com.pages

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import devxertech.com.component.CommonButton
import devxertech.com.pages.commons.CommonHeader
import devxertech.com.pages.dashboard.footerSection.HomepageFooter
import devxertech.com.pages.services.chatbotDevelopment.AiAssistantDemo
import devxertech.com.section.DesktopNavigationAnchor
import devxertech.com.seo.PageMetadata
import devxertech.com.theme.toSitePalette
import devxertech.com.utils.Constants
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

private data class Solution(val title: String, val description: String, val path: String)

private val solutions = listOf(
    Solution("AI Chatbots & Assistants", "Help visitors find answers and take the next step with an assistant built around your approved business information.", "/chatbot-development"),
    Solution("Customer Support Assistants", "Answer routine questions and guide unresolved requests to your team through an agreed support process.", "/chatbot-development"),
    Solution("Lead Qualification Assistants", "Collect relevant inquiry details and help your team understand what a potential customer needs.", "/chatbot-development"),
    Solution("Workflow Automation", "Reduce repetitive work by connecting supported systems and automating clearly defined tasks, subject to API access and feasibility.", "/contact-us"),
    Solution("Internal Knowledge Assistants", "Help employees find relevant information across approved documents, policies and internal resources, with appropriate access controls.", "/contact-us"),
)

@Page("/solutions")
@Composable
fun SolutionsPage() {
    PageMetadata(
        "Custom AI Solutions for Businesses | DevxerTech",
        "Explore DevxerTech’s custom AI chatbots, customer support assistants, lead qualification and workflow automation solutions for your business."
    )
    val palette = ColorMode.current.toSitePalette()
    val breakpoint = rememberBreakpoint()
    val compact = breakpoint < Breakpoint.MD

    Style {
        ".solutions-page *" { property("box-sizing", "border-box") }
        ".solutions-page a:focus-visible, .solutions-page video:focus-visible" {
            property("outline", "3px solid ${palette.commonSecondaryButtonColor.rgb}")
            property("outline-offset", "6px")
        }
        ".solution-row" {
            property("display", "grid")
            property("grid-template-columns", if (compact) "40px minmax(0, 1fr) 28px" else "64px minmax(0, 1fr) minmax(0, 1fr) 40px")
            property("gap", if (compact) "12px" else "32px")
            property("align-items", "start")
            property("padding", if (compact) "30px 0" else "44px 0")
            property("border-top", "1px solid ${palette.textFieldBorderColor.rgb}")
            property("color", "inherit")
            property("transition", "background-color 180ms ease")
        }
        ".solution-row:hover, .solution-row:focus-visible" {
            property("background-color", "${palette.softwareSectionBackground.rgb}")
        }
        ".solution-arrow" { property("transition", "transform 180ms ease") }
        ".solution-row:hover .solution-arrow" { property("transform", "translateX(4px)") }
        "@media (prefers-reduced-motion: reduce)" {
            ".solution-row, .solution-arrow" { property("transition", "none") }
        }
    }
    Div(attrs = {
        classes("solutions-page")
        style {
            width(100.percent)
            backgroundColor(palette.bodyBackgroundSecondary.rgb)
            color(palette.commonSecondaryButtonColor.rgb)
            fontFamily(Constants.FONT_FAMILY)
        }
    }) {
        CommonHeader(
            image = "/devxertech-ai-solutions-hero.svg",
            breakpoint = breakpoint,
            headingText = "AI Solutions for Your Business",
            descriptionText = "Turn repetitive tasks and everyday questions into useful AI experiences. We build custom chatbots, assistants and automation around your business information and workflows.",
            mainHeadingAsH1 = true,
            startProjectText = "Discuss Your Project",
            startProjectButtonWidth = 230,
            preserveImageAspectRatio = true,
            imageAlt = "AI assistants connecting business information and workflows",
        )
        Main {
            Section(attrs = {
                attr("aria-labelledby", "solution-categories-heading")
                style { padding(if (compact) 56.px else 88.px, if (compact) 24.px else 40.px) }
            }) {
                Div(attrs = { style { property("max-width", "1100px"); property("margin", "0 auto") } }) {
                    H2(attrs = { id("solution-categories-heading"); style {
                        fontFamily(Constants.ROBOTO_FONT); fontSize(if (compact) 28.px else 36.px)
                        fontWeight(700); margin(0.px, 0.px, 36.px)
                    } }) { Text("Solutions Built Around Your Business") }
                    solutions.forEachIndexed { index, solution ->
                        DesktopNavigationAnchor(solution.path, Modifier.attrsModifier { classes("solution-row") }) {
                            Span(attrs = { attr("aria-hidden", "true"); style {
                                color(palette.primary.rgb); fontSize(18.px); fontWeight(600); paddingTop(4.px)
                            } }) { Text((index + 1).toString().padStart(2, '0')) }
                            H3(attrs = { style {
                                fontFamily(Constants.ROBOTO_FONT); fontSize(if (compact) 23.px else 27.px)
                                lineHeight("1.3"); fontWeight(600); margin(0.px)
                            } }) { Text(solution.title) }
                            P(attrs = { style {
                                property("grid-column", if (compact) "2" else "auto")
                                property("grid-row", if (compact) "2" else "auto")
                                fontSize(17.px); lineHeight("1.75"); margin(0.px)
                            } }) { Text(solution.description) }
                            Span(attrs = { classes("solution-arrow"); attr("aria-hidden", "true"); style {
                                property("grid-column", if (compact) "3" else "4")
                                property("grid-row", "1"); fontSize(28.px); color(palette.primary.rgb)
                            } }) { Text("→") }
                        }
                    }
                }
            }
            AiAssistantDemo(breakpoint, palette, showProjectCta = false)
            Section(attrs = { attr("aria-labelledby", "solutions-cta-heading"); style {
                padding(if (compact) 64.px else 96.px, if (compact) 24.px else 40.px)
                textAlign("center")
            } }) {
                Div(attrs = { style {
                    property("max-width", "760px"); property("margin", "0 auto")
                    display(DisplayStyle.Flex); property("flex-direction", "column"); property("align-items", "center")
                } }) {
                    H2(attrs = { id("solutions-cta-heading"); style {
                        fontFamily(Constants.ROBOTO_FONT); fontSize(if (compact) 30.px else 40.px)
                        lineHeight("1.25"); margin(0.px, 0.px, 20.px)
                    } }) { Text("Have a Workflow You Want to Improve?") }
                    P(attrs = { style { fontSize(18.px); lineHeight("1.7"); margin(0.px, 0.px, 32.px) } }) {
                        Text("Tell us what your team handles today. We’ll help define a practical starting scope for an AI solution.")
                    }
                    CommonButton("Discuss Your AI Project", onClick = {}, linkPath = "/contact-us", buttonWidth = 250)
                }
            }
        }
        HomepageFooter(breakpoint)
    }
}
